// src/components/OralAnswer.js
import React, { useEffect, useRef, useState } from 'react';
import { uploadAnswerBlob } from '../../utils/uploadAnswer';

function fmt(tSec){
    const s = Math.max(0, Math.floor(tSec));
    const mm = Math.floor(s/60);
    const ss = s%60;
    return `${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
}

/**
 * OralAnswer
 * Props:
 *  - attemptId, questionId  (wymagane do uploadu)
 *  - onSaved({ file_key, meta }) (opcjonalne)
 *  - disabled (opcjonalne)
 *  - mode: 'audio' | 'av' (domyślnie 'audio')
 */
export default function OralAnswer({
                                       attemptId,
                                       questionId,
                                       onSaved,
                                       disabled = false,
                                       mode = 'audio',
                                   }){
    const [status, setStatus] = useState('idle'); // idle | recording | review | saving
    const [err, setErr] = useState('');
    const [elapsed, setElapsed] = useState(0);
    const [blobUrl, setBlobUrl] = useState('');
    const [blob, setBlob] = useState(null);
    const [savedMsg, setSavedMsg] = useState(''); // << NEW

    const mediaRef = useRef({ stream:null, rec:null, chunks:[], startedAt:0 });
    const rafRef = useRef(null);
    const meterCanvasRef = useRef(null);
    const analyserRef = useRef(null);
    const audioCtxRef = useRef(null);
    const srcNodeRef = useRef(null);
    const timerRef = useRef(null);
    const savedTimerRef = useRef(null);

    // preferencje MIME
    const AUDIO_MIME = (()=>{
        if (window.MediaRecorder?.isTypeSupported?.('audio/webm;codecs=opus')) return { mime:'audio/webm;codecs=opus', ct:'audio/webm' };
        if (window.MediaRecorder?.isTypeSupported?.('audio/webm'))            return { mime:'audio/webm', ct:'audio/webm' };
        if (window.MediaRecorder?.isTypeSupported?.('audio/ogg;codecs=opus')) return { mime:'audio/ogg;codecs=opus', ct:'audio/ogg' };
        return { mime: '', ct:'application/octet-stream' };
    })();

    const AV_MIME = (()=>{
        if (window.MediaRecorder?.isTypeSupported?.('video/webm;codecs=vp8,opus')) return { mime:'video/webm;codecs=vp8,opus', ct:'video/webm' };
        if (window.MediaRecorder?.isTypeSupported?.('video/webm'))                 return { mime:'video/webm', ct:'video/webm' };
        return { mime:'', ct:'application/octet-stream' };
    })();

    function cleanupStream(){
        try{ mediaRef.current.stream?.getTracks()?.forEach(t=>t.stop()); }catch{}
        mediaRef.current.stream = null;
    }
    function cleanupRecorder(){
        mediaRef.current.rec = null;
        mediaRef.current.chunks = [];
        mediaRef.current.startedAt = 0;
    }
    function cleanupAudioGraph(){
        try{ rafRef.current && cancelAnimationFrame(rafRef.current); }catch{}
        rafRef.current = null;
        try{ srcNodeRef.current && srcNodeRef.current.disconnect(); }catch{}
        try{ analyserRef.current && analyserRef.current.disconnect(); }catch{}
        try{ audioCtxRef.current && audioCtxRef.current.close(); }catch{}
        srcNodeRef.current = null;
        analyserRef.current = null;
        audioCtxRef.current = null;
    }
    function cleanupTimer(){
        if (timerRef.current){ clearInterval(timerRef.current); timerRef.current = null; }
    }
    function resetReview(){
        if (blobUrl){ URL.revokeObjectURL(blobUrl); }
        setBlobUrl(''); setBlob(null);
    }

    useEffect(()=>()=>{ // unmount
        cleanupTimer();
        cleanupAudioGraph();
        cleanupRecorder();
        cleanupStream();
        resetReview();
        if (savedTimerRef.current){ clearTimeout(savedTimerRef.current); }
    }, []);

    async function startRec(){
        if (disabled || status === 'recording') return;
        setErr('');
        setSavedMsg(''); // wyczyść poprzedni komunikat
        resetReview();

        const constraints = mode === 'av'
            ? { audio: { echoCancellation:true, noiseSuppression:true, autoGainControl:true },
                video: { width:{ideal:640}, height:{ideal:480}, frameRate:{ideal:24} } }
            : { audio: { echoCancellation:true, noiseSuppression:true, autoGainControl:true } };

        let stream;
        try{
            stream = await navigator.mediaDevices.getUserMedia(constraints);
        }catch(e){
            setErr(`Brak dostępu do mikrofonu${mode==='av'?'/kamery':''}: ${e?.message||e}`);
            return;
        }
        mediaRef.current.stream = stream;

        // Audio meter
        try{
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            audioCtxRef.current = ctx;
            const src = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            src.connect(analyser);
            analyserRef.current = analyser;
            srcNodeRef.current = src;
            drawMeter();
        }catch{ /* brak wizualizacji ≠ błąd krytyczny */ }

        // MediaRecorder
        const mimeCfg = mode === 'av' ? AV_MIME : AUDIO_MIME;
        let rec;
        try{
            rec = new MediaRecorder(stream, mimeCfg.mime ? { mimeType: mimeCfg.mime } : undefined);
        }catch(e){
            setErr(`MediaRecorder nieobsługiwany: ${e?.message||e}`);
            cleanupAudioGraph(); cleanupStream();
            return;
        }
        mediaRef.current.rec = rec;
        mediaRef.current.chunks = [];
        mediaRef.current.startedAt = Date.now();

        rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) mediaRef.current.chunks.push(ev.data); };
        rec.onerror = (ev) => { setErr(`Błąd nagrywania: ${ev?.error?.message||ev?.name||'unknown'}`); };
        rec.onstop = () => {
            cleanupTimer();
            cleanupAudioGraph();
            setStatus('review');
            const out = new Blob(mediaRef.current.chunks, { type: mimeCfg.ct });
            setBlob(out);
            setBlobUrl(URL.createObjectURL(out));
        };

        try{
            rec.start();
            setStatus('recording');
            setElapsed(0);
            timerRef.current = setInterval(()=>{
                const ms = Date.now() - mediaRef.current.startedAt;
                setElapsed(ms/1000);
            }, 250);
        }catch(e){
            setErr(`Start nagrywania nieudany: ${e?.message||e}`);
            cleanupAudioGraph(); cleanupStream(); cleanupRecorder();
        }
    }

    function stopRec(){
        if (status !== 'recording') return;
        try{ mediaRef.current.rec?.stop(); }catch{}
        cleanupStream();
    }

    function drawMeter(){
        const cvs = meterCanvasRef.current;
        const an = analyserRef.current;
        if (!cvs || !an) return;
        const ctx = cvs.getContext('2d');
        const data = new Uint8Array(an.frequencyBinCount);
        const W = cvs.width, H = cvs.height;

        const loop = ()=>{
            rafRef.current = requestAnimationFrame(loop);
            an.getByteTimeDomainData(data);

            let sum = 0;
            for (let i=0;i<data.length;i++){
                const v = (data[i]-128)/128;
                sum += v*v;
            }
            const rms = Math.sqrt(sum/data.length);
            const bar = Math.min(1, rms * 3);

            ctx.clearRect(0,0,W,H);
            ctx.fillStyle = '#f3f3f3';
            ctx.fillRect(0,0,W,H);
            ctx.fillStyle = '#ddd';
            ctx.fillRect(0,H-12,W,12);
            ctx.fillStyle = '#2a8';
            ctx.fillRect(0,H-12,Math.floor(W*bar),12);
            ctx.fillStyle = '#e33';
            ctx.fillRect(Math.floor(W*bar)-2,H-14,4,16);
        };
        loop();
    }

    async function save(){
        if (!blob) return;
        setErr('');
        setStatus('saving');
        try{
            const filename = `${mode==='av'?'oral-av':'oral-audio'}-${questionId}-${Date.now()}.webm`;
            const contentType = mode==='av' ? (AV_MIME.ct || 'video/webm') : (AUDIO_MIME.ct || 'audio/webm');
            const meta = { kind: mode==='av' ? 'oral_av' : 'oral_audio', duration_sec: Math.round(elapsed) };

            const { file_key, meta: savedMeta } = await uploadAnswerBlob({
                attemptId, questionId, blob, filename, contentType, extraMeta: meta
            });

            onSaved?.({ file_key, meta: savedMeta });

            // Czyść podgląd, wróć do idle i pokaż komunikat
            setStatus('idle');
            resetReview();
            setElapsed(0);
            setSavedMsg('Zapisano odpowiedź.');        // << NEW
            if (savedTimerRef.current){ clearTimeout(savedTimerRef.current); }
            savedTimerRef.current = setTimeout(()=> setSavedMsg(''), 3000); // auto-hide po 3 s
        }catch(e){
            setErr(e.message || 'Błąd zapisu nagrania');
            setStatus('review');
        }
    }

    function discard(){
        resetReview();
        setStatus('idle');
        setElapsed(0);
    }

    return (
        <div style={{ display:'grid', gap:12 }}>
            {/* Komunikat sukcesu */}
            {savedMsg && (
                <div
                    role="status"
                    aria-live="polite"
                    style={{
                        padding:'8px 10px',
                        borderRadius:8,
                        background:'#e9f8ef',
                        color:'#0a6836',
                        border:'1px solid #bfe8cd',
                        fontSize:14
                    }}
                >
                    {savedMsg}
                </div>
            )}

            {/* Pasek nagrywania */}
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ minWidth:140, fontWeight:600 }}>
                    {status === 'recording' ? (
                        <span style={{ color:'#d22' }}>● Nagrywam</span>
                    ) : status === 'review' ? (
                        <span>Podgląd nagrania</span>
                    ) : status === 'saving' ? (
                        <span>Zapisywanie…</span>
                    ) : (
                        <span>Gotowe do nagrania</span>
                    )}
                </div>
                <div style={{ fontVariantNumeric:'tabular-nums', minWidth:60 }}>
                    {fmt(elapsed)}
                </div>
                <canvas
                    ref={meterCanvasRef}
                    width={160}
                    height={28}
                    style={{ width:160, height:28, borderRadius:6, background:'#f3f3f3' }}
                />
            </div>

            {/* Kontrolki */}
            {status !== 'recording' && status !== 'saving' && (
                <button  className={'but-smal'}  onClick={startRec} disabled={disabled} style={{ width:200 }}>
                    {mode==='av' ? 'Nagraj (audio+video)' : 'Nagraj (audio)'}
                </button>
            )}
            {status === 'recording' && (
                <button  className={'but-smal'} onClick={stopRec} style={{ width:200, background:'#d22', color:'#fff' }}>
                    Zatrzymaj
                </button>
            )}

            {/* Podgląd po nagraniu */}
            {status === 'review' && blobUrl && (
                <div style={{ display:'grid', gap:8 }}>
                    {mode==='av' ? (
                        <video src={blobUrl} controls style={{ width:'100%', borderRadius:8, background:'#000' }} />
                    ) : (
                        <audio src={blobUrl} controls />
                    )}
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                        <button  className={'but-smal'}  onClick={save} style={{ background:'#2a8', color:'#fff'}}>Zapisz odpowiedź</button>
                        <button  className={'but-smal'} onClick={discard}>Nagraj ponownie</button>
                    </div>
                </div>
            )}

            {err && (
                <div style={{ color:'#a00' }}>
                    {err}
                </div>
            )}

            <small style={{ opacity:0.8, fontSize:18, marginTop: 20, color:'red' }}>
                Uwaga: Po nagraniu należy zapisać odpowiedź przed przejściem do następnego pytania
            </small>
        </div>
    );
}
